import {
  FEE_LIFECYCLE_STAGE,
  FEE_PARTY_ROLE,
  FEE_RECURRENCE,
  FEE_SNAPSHOT_SOURCE,
  FEE_VALUE_TYPE,
} from '@shared/constants/fees'
import type {
  AnnualRunningCostDto,
  FeeLineItemDto,
  OfferingPriceDto,
  PropertyFeeRowDto,
  PropertyFeeVersionDto,
} from './propertyFeesContracts'
import type {
  AnnualRunningCost,
  FeeLineItem,
  OfferingPrice,
  PropertyFee,
  PropertyFeeScreen,
  PropertyFeeVersion,
} from '../types/propertyFeeTypes'

const ZERO = '0'

export function parsePropertyFee(dto: PropertyFeeRowDto): PropertyFee {
  return {
    definitionId: String(dto.definitionId ?? ''),
    code: String(dto.code ?? ''),
    name: String(dto.name ?? ''),
    description: dto.description ?? null,
    lifecycleStage: dto.lifecycleStage ?? FEE_LIFECYCLE_STAGE.ONBOARDING,
    paidByRole: dto.paidByRole ?? FEE_PARTY_ROLE.INVESTOR,
    paidToRole: dto.paidToRole ?? FEE_PARTY_ROLE.PLATFORM,
    recurrence: dto.recurrence ?? FEE_RECURRENCE.ONE_TIME,
    isCustomisable: dto.isCustomisable !== false,
    defaultValue: String(dto.defaultValue ?? ZERO),
    defaultValueType: dto.defaultValueType ?? dto.valueType ?? FEE_VALUE_TYPE.FLAT,
    defaultUnitBasis: dto.defaultUnitBasis ?? null,
    valueType: dto.valueType ?? FEE_VALUE_TYPE.FLAT,
    valueBasis: dto.valueBasis ?? null,
    value: String(dto.value ?? ZERO),
    unitBasis: dto.unitBasis ?? null,
    unitQuantity: dto.unitQuantity ?? null,
    isVatApplicable: Boolean(dto.isVatApplicable),
    vatPercentage: dto.vatPercentage ?? null,
    netAmount: String(dto.netAmount ?? ZERO),
    vatAmount: String(dto.vatAmount ?? ZERO),
    grossAmount: String(dto.grossAmount ?? ZERO),
    isCapitalisedIntoOfferingPrice: Boolean(dto.isCapitalisedIntoOfferingPrice),
    propertyFeeId: dto.propertyFeeId ?? null,
    snapshotSource: dto.snapshotSource ?? null,
    overrideReason: dto.overrideReason ?? null,
    effectiveFrom: dto.effectiveFrom ?? null,
    isOverridden: Boolean(dto.isOverridden),
  }
}

function parseFeeLineItem(dto: FeeLineItemDto): FeeLineItem {
  return {
    definitionId: String(dto.definitionId ?? ''),
    code: String(dto.code ?? ''),
    name: String(dto.name ?? ''),
    valueType: dto.valueType ?? FEE_VALUE_TYPE.FLAT,
    value: String(dto.value ?? ZERO),
    netAmount: String(dto.netAmount ?? ZERO),
    vatAmount: String(dto.vatAmount ?? ZERO),
    grossAmount: String(dto.grossAmount ?? ZERO),
  }
}

/**
 * An unresolvable breakdown is the normal state of a property whose purchase
 * price has not been entered yet, so it parses to a zeroed shape rather than
 * null — the panel renders the reason instead of disappearing.
 */
export function parseOfferingPrice(data: unknown): OfferingPrice {
  const dto = (unwrap(data) ?? {}) as OfferingPriceDto
  const lineItems = Array.isArray(dto.lineItems) ? dto.lineItems : []
  return {
    isResolvable: Boolean(dto.isResolvable),
    purchasePrice: dto.purchasePrice ?? null,
    lineItems: lineItems.map(parseFeeLineItem),
    capitalisedFeesTotal: String(dto.capitalisedFeesTotal ?? ZERO),
    offeringPrice: dto.offeringPrice ?? null,
  }
}

/**
 * A property with no running-cost fee parses to an empty breakdown totalling
 * zero — the shape a caller reads "this market defines no running cost" off,
 * rather than null, which would make every consumer re-check before summing.
 */
export function parseAnnualRunningCost(data: unknown): AnnualRunningCost {
  const dto = (unwrap(data) ?? {}) as AnnualRunningCostDto
  const lineItems = Array.isArray(dto.lineItems) ? dto.lineItems : []
  return {
    lineItems: lineItems.map(parseFeeLineItem),
    total: String(dto.total ?? ZERO),
  }
}

/**
 * The fee list, the offering-price breakdown and the annual running cost arrive
 * together because a fee change moves all three — the screen must never show one
 * without the others.
 */
export function parsePropertyFeeScreen(data: unknown): PropertyFeeScreen {
  const payload = (unwrap(data) ?? {}) as Record<string, unknown>
  const rows = Array.isArray(payload.rows) ? (payload.rows as PropertyFeeRowDto[]) : []
  const rowsCount = typeof payload.rowsCount === 'number' ? payload.rowsCount : rows.length
  return {
    rows: rows.map(parsePropertyFee),
    rowsCount,
    offeringPrice: parseOfferingPrice(payload.offeringPrice),
    annualRunningCost: parseAnnualRunningCost(payload.annualRunningCost),
  }
}

export function parsePropertyFeeVersion(dto: PropertyFeeVersionDto): PropertyFeeVersion {
  return {
    id: String(dto.id ?? ''),
    valueType: dto.valueType ?? FEE_VALUE_TYPE.FLAT,
    value: String(dto.value ?? ZERO),
    unitBasis: dto.unitBasis ?? null,
    unitQuantity: dto.unitQuantity ?? null,
    isVatApplicable: Boolean(dto.isVatApplicable),
    vatPercentage: dto.vatPercentage ?? null,
    snapshotSource: dto.snapshotSource ?? FEE_SNAPSHOT_SOURCE.DEFAULT_FROM_MASTER,
    overrideReason: dto.overrideReason ?? null,
    effectiveFrom: String(dto.effectiveFrom ?? ''),
    effectiveTo: dto.effectiveTo ?? null,
    createdBy: dto.createdBy ?? null,
  }
}

export function parsePropertyFeeVersions(data: unknown): PropertyFeeVersion[] {
  const payload = unwrap(data)
  if (Array.isArray(payload))
    return (payload as PropertyFeeVersionDto[]).map(parsePropertyFeeVersion)
  const rows = (payload as { rows?: unknown } | null)?.rows
  if (!Array.isArray(rows)) return []
  return (rows as PropertyFeeVersionDto[]).map(parsePropertyFeeVersion)
}

function unwrap(data: unknown): unknown {
  if (!data || typeof data !== 'object') return null
  const record = data as Record<string, unknown>
  return record.data ?? record
}
